import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Meeting } from 'util/constants';
import { useUser } from 'util/UserContext';
import ScrollableTabsList from 'ui/ScrollableTabsList';
import api from 'config/axiosConfig';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import EntityAvatar from 'components/Entities/EntityAvatar';
import { Users, MessageSquare } from 'lucide-react';
import CommentsSection from 'components/Comments/CommentsSection';
//import { ActorSuggestion } from 'components/Common/ActorSearch';
import { getActorDetails } from 'util/getObjectDetails';
import ScrollHideContainer from 'ui/ScrollHideContainer';

interface Props {
  meeting: Meeting;
}

// Interface for participant with loaded user details
interface ParticipantWithUser {
  id: number;
  participant_id: number;
  properties?: any;
  user: any; // User details from API
  isLoading: boolean;
}

const MeetingProfileTabs: React.FC<Props> = ({ meeting }) => {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const { currentUser } = useUser();
  const [selectedTab, setSelectedTab] = useState<string>(tab || 'comments');
  const [participants, setParticipants] = useState<ParticipantWithUser[]>([]);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState<boolean>(false);
  const [isOrganizer, setIsOrganizer] = useState<boolean>(false);

  // Update selectedTab when URL tab parameter changes
  useEffect(() => {
    if (tab) {
      setSelectedTab(tab);
    } else {
      setSelectedTab('comments');
    }
  }, [tab]);
  
  useEffect(() => {
    if (currentUser && meeting) {
      // Check if current user is an organizer
      api.get(`/api/meetings/${meeting.id}/organizer-status`)
        .then(res => setIsOrganizer(res.data.isOrganizer))
        .catch(err => console.error('Error checking organizer status:', err));
    }
  }, [meeting, currentUser]);

  useEffect(() => {
    if (selectedTab === 'participants' && meeting) {
      fetchParticipants();
    }
    //eslint-disable-next-line
  }, [selectedTab, meeting]);

  const fetchParticipants = async () => {
    setIsLoadingParticipants(true);
    try {
      const response = await api.get(`/api/meetings/${meeting.id}/participants`);
      
      // First, create participant objects with loading state
      const participantsWithLoadingState = response.data.participants.map((participant: any) => ({
        ...participant,
        user: null,
        isLoading: true
      }));
      
      setParticipants(participantsWithLoadingState);
      
      // Then fetch user details for each participant
      const participantsWithUserDetails = await Promise.all(
        response.data.participants.map(async (participant: any) => {
          try {
            const userDetails = await getActorDetails('user', participant.participant_id);
            return {
              ...participant,
              user: userDetails,
              isLoading: false
            };
          } catch (error) {
            console.error(`Error fetching details for user ${participant.participant_id}:`, error);
            return {
              ...participant,
              user: null,
              isLoading: false
            };
          }
        })
      );
      
      setParticipants(participantsWithUserDetails);
    } catch (error) {
      console.error('Error fetching participants:', error);
    } finally {
      setIsLoadingParticipants(false);
    }
  };

  const tabs = [
    { 
      label: 'Comments', 
      value: 'comments',
      icon: <MessageSquare className="h-4 w-4" />
    },
    { 
      label: `Participants`, 
      value: 'participants',
      icon: <Users className="h-4 w-4" />
    }
  ];

  const handleParticipationRequest = async (participantId: number, accept: boolean) => {
    if (!isOrganizer) return;
    
    try {
      // We'd need to get the request ID for this participant
      // This is a simplified version - you'd need to implement request fetching
      const requestsResponse = await api.get(`/api/requests`, {
        params: {
          entityType: 'meeting',
          entityId: meeting.id,
          requestType: 'meeting_participation_request',
          requestorId: participantId,
          requestorType: 'user',
          status: 'pending'
        }
      });
      
      if (requestsResponse.data.requests.length > 0) {
        const requestId = requestsResponse.data.requests[0].id;
        await api.patch(`/api/meetings/${meeting.id}/participation/${requestId}`, {
          accept
        });
        // Refresh participants list
        fetchParticipants();
      }
    } catch (error) {
      console.error('Error handling participation request:', error);
    }
  };
  
  /* Convert ActorSuggestion to User type for EntityAvatar
  const convertToUser = (actor: ActorSuggestion): any => ({
      id: actor.id,
      id_short: actor.id.toString(),
      username: actor.username || actor.name,
      login_name: actor.username || actor.name,
      display_name: actor.display_name || actor.name,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      about: {},
      properties_public: {
          ...actor.properties_public
      }
  });*/

  const ParticipantsTab = () => (
    <div className="py-4">
      {isLoadingParticipants ? (
        <LoadingSkeleton count={3} />
      ) : participants.length === 0 ? (
        <div className="text-center py-8 text-eoe-text-sub dark:text-eoe-dark-text-sub">
          Be the first to join!
        </div>
      ) : (
        <div className="space-y-4">
          {participants.map((participant) => (
            <div 
              key={participant.id} 
              className="flex items-center justify-between p-3 rounded-md bg-light-box-bg dark:bg-eoe-dark-bg-tertiary"
            >
              {participant.isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover animate-pulse"></div>
                  <div className="h-4 w-24 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover animate-pulse rounded"></div>
                </div>
              ) : participant.user ? (
                <EntityAvatar
                  entity={participant.user}
                  entityType="user"
                  showLink={true}
                />
              ) : (
                <div className="text-eoe-text-sub dark:text-eoe-dark-text-sub">
                  User ID: {participant.participant_id}
                </div>
              )}
              
              {isOrganizer && participant.properties?.status === 'pending' && (
                <div className="flex space-x-2">
                  <button 
                    className="btn-primary-sm"
                    onClick={() => handleParticipationRequest(participant.participant_id, true)}
                  >
                    Accept
                  </button>
                  <button 
                    className="btn-secondary-sm"
                    onClick={() => handleParticipationRequest(participant.participant_id, false)}
                  >
                    Decline
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="py-0 relative">
      <ScrollHideContainer 
        className="z-50 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg"
        hideOffset={57}
        threshold={56}
      >
      <div className="p-2">
        <ScrollableTabsList 
            tabs={tabs}
            selectedTab={selectedTab}
            onTabChange={setSelectedTab}
            pageTitle={meeting.title}
        />
      </div>
      </ScrollHideContainer>
      <div className="mt-4">
        {selectedTab === 'participants' && <ParticipantsTab />}
        
        {selectedTab === 'comments' && (
          <CommentsSection 
            commentable_id={Number(id)}
            commentable_type="meeting"
            isEntitySection={true}
          />
        )}
      </div>
    </div>
  );
};

export default MeetingProfileTabs;
